import { Box, BoxProps, Typography } from '@mui/material';
import {
  AnimatedStackedBar,
  AnimatedStackedBarData,
} from 'components/common/CustomStackedBarChart';
import { Tooltip } from 'components/common/Tooltip';
import { IconLinearArrowRight } from 'components/icons/components/linear/IconLinearArrowRight';
import { getColorBySentiment } from 'features/socialListeningAnalytics/views/slaBrandInboundSentiment/types';
import { SentimentBarColorMap } from 'features/socialListeningCommunity';
import { motion } from 'framer-motion';
import {
  BrandInboundFiltersInput,
  Sentiment,
  SentimentMedium,
} from 'graphql/generated';
import { KeyInsightItem } from 'pages/socialMediaListening/sentiment/sections/keyInsights/components/KeyInsightCollapsibleCard/types';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlotRoutes } from 'Routes';
import { theme } from 'styles/theme';

export type SentimentRateCardProps = {
  sentiment: Sentiment;
  rate: string;
};

export const SentimentRateCard = ({
  sentiment,
  rate,
}: SentimentRateCardProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: theme.spacing(1),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 28,
        }}
      >
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: SentimentBarColorMap[sentiment],
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            height: 28,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="headline-xs"
            textTransform="capitalize"
            color={theme.colors?.utility[700]}
          >
            {sentiment.charAt(0).toUpperCase() +
              sentiment.slice(1).toLowerCase()}
          </Typography>
        </Box>
        <Typography
          color={theme.colors?.primary.black}
          variant="headline-md"
          fontSize={20}
        >
          {rate}%
        </Typography>
      </Box>
    </Box>
  );
};

export type AnimatedKeyInsightItemWithTooltipProps = {
  keyInsight: KeyInsightItem;
  index: number;
  filters: BrandInboundFiltersInput;
  brandId: string;
  medium: SentimentMedium;
};

export const AnimatedKeyInsightItemWithTooltip = ({
  keyInsight,
  index,
  filters,
  brandId,
  medium,
}: AnimatedKeyInsightItemWithTooltipProps) => {
  const navigate = useNavigate();
  // calc sentiment rates
  // output is a string with 2 decimal places
  // if the rate is 0, return '0'
  const { positiveRate, neutralRate, negativeRate } = useMemo(() => {
    const totalMentionCount = keyInsight.sentiments.reduce(
      (acc, sentiment) => acc + sentiment.commentCount,
      0,
    );
    if (totalMentionCount === 0) {
      return {
        positiveRate: '0',
        neutralRate: '0',
        negativeRate: '0',
      };
    }
    const positiveRate =
      (keyInsight.sentiments.find(
        (sentiment) => sentiment.sentiment === Sentiment.Positive,
      )?.commentCount ?? 0) / totalMentionCount;
    const neutralRate =
      (keyInsight.sentiments.find(
        (sentiment) => sentiment.sentiment === Sentiment.Neutral,
      )?.commentCount ?? 0) / totalMentionCount;
    const negativeRate =
      (keyInsight.sentiments.find(
        (sentiment) => sentiment.sentiment === Sentiment.Negative,
      )?.commentCount ?? 0) / totalMentionCount;
    return {
      positiveRate: positiveRate > 0 ? positiveRate.toFixed(2) : '0',
      neutralRate: neutralRate > 0 ? neutralRate.toFixed(2) : '0',
      negativeRate: negativeRate > 0 ? negativeRate.toFixed(2) : '0',
    };
  }, [keyInsight]);

  return (
    <Tooltip
      sx={{
        bgcolor: 'transparent',
      }}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'transparent',
            border: 'none',
          },
        },
      }}
      title={
        <Box
          sx={{
            padding: theme.spacing(6),
            backgroundColor: theme.colors?.primary.white,
            boxShadow:
              '0px 18px 88px -4px rgba(24, 39, 75, 0.12), 0px 8px 28px -6px rgba(24, 39, 75, 0.1)',
            backdropFilter: 'blur(40px)',
            borderRadius: theme.spacing(4),
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: theme.spacing(6),
          }}
        >
          <SentimentRateCard
            sentiment={Sentiment.Positive}
            rate={positiveRate}
          />
          <SentimentRateCard sentiment={Sentiment.Neutral} rate={neutralRate} />
          <SentimentRateCard
            sentiment={Sentiment.Negative}
            rate={negativeRate}
          />
          <Box
            sx={{
              padding: theme.spacing(2),
              backgroundColor: theme.colors?.primary.white,
              '&:hover': {
                backgroundColor: theme.colors?.utility[300],
              },
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${theme.colors?.utility[400]}`,
              borderRadius: theme.spacing(1.5),
              width: 28,
              height: 28,
            }}
            onClick={() => {
              navigate(PlotRoutes.socialListeningSentiment(brandId), {
                state: {
                  filters: {
                    ...filters,
                  },
                  medium,
                },
              });
            }}
          >
            <IconLinearArrowRight
              size={12}
              color={theme.colors?.primary.black}
            />
          </Box>
        </Box>
      }
      placement="top"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.3, duration: 0.5 }}
        style={{ width: '100%' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(2),
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: theme.spacing(2),
              border: `2px solid ${theme.colors?.utility[300]}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="headline-lg"
              color={theme.colors?.utility[700]}
            >
              {keyInsight.emoji}
            </Typography>
          </Box>
          <AnimatedStackedBar
            onlyRoundedCorners
            data={getKeyInsightDataForAnimatedStackedBar(keyInsight)}
            renderOverlayContent={() => {
              const totalMentionCount = keyInsight.sentiments.reduce(
                (acc, sentiment) => acc + sentiment.commentCount,
                0,
              );
              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: theme.spacing(4),
                    px: theme.spacing(4),
                    py: theme.spacing(2),
                  }}
                >
                  <Typography
                    textTransform="capitalize"
                    variant="headline-sm"
                    color={theme.colors?.utility[1000]}
                  >
                    {keyInsight.title}
                  </Typography>
                  <Typography
                    variant="headline-xs"
                    color={theme.colors?.utility[800]}
                  >
                    {totalMentionCount} Mentions
                  </Typography>
                </Box>
              );
            }}
            componentsProps={{
              container: {
                sx: {
                  width: '100%',
                  height: 46,
                },
              },
            }}
            barBorderRadius={10}
          />
        </Box>
      </motion.div>
    </Tooltip>
  );
};

// item for each bar
export const getKeyInsightDataForAnimatedStackedBar = (
  keyInsight: KeyInsightItem,
): AnimatedStackedBarData => {
  const items = keyInsight.sentiments.map((sentiment) => {
    const colorBySentiment = getColorBySentiment(sentiment.sentiment);
    return {
      value: sentiment.commentCount,
      color: colorBySentiment,
    };
  });
  return { items };
};

export type SLASentimentKeyInsightListData = {
  keyInsights: KeyInsightItem[];
};

export type SLASentimentKeyInsightListProps = {
  componentsProps?: {
    container?: BoxProps;
  };
  data: SLASentimentKeyInsightListData;
  emptyStateText?: string;
  filters: BrandInboundFiltersInput;
  brandId: string;
  medium: SentimentMedium;
};

export const SLASentimentKeyInsightList = ({
  componentsProps,
  data,
  emptyStateText = 'No key insights available at the moment:(',
  filters,
  brandId,
  medium,
}: SLASentimentKeyInsightListProps) => {
  const { keyInsights = [] } = data;

  // render empty state if the key insights list is empty
  if (keyInsights.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          height: '100%',
        }}
      >
        <Typography
          variant="body-lg"
          color={theme.colors?.utility[700]}
          display="flex"
          justifyContent="center"
          width="100%"
        >
          {emptyStateText}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      {...componentsProps?.container}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(6),
        overflow: 'auto',
        height: '100%',
        flex: 1,
        ...componentsProps?.container?.sx,
      }}
    >
      {keyInsights.map((keyInsight, index) => (
        <AnimatedKeyInsightItemWithTooltip
          key={keyInsight.id}
          keyInsight={keyInsight}
          index={index}
          filters={filters}
          brandId={brandId}
          medium={medium}
        />
      ))}
    </Box>
  );
};
